import { useState } from 'react';
import type { ReactNode } from 'react';
import { TIP_PERCENT_CHIPS, tipFromPercent } from '@yourstore/domain';
import { formatMoney } from '../../mirror/time';
import type { T, UiLocale } from '../../messages';
import type { Certificate, CertificateState, RemainderKind } from '../../wire';
import type { TenderDraft } from './model';

/**
 * **"How was it paid?"** — big buttons for the ways this shop takes money, and
 * the less common things behind quiet links.
 *
 * The buttons are `session.till.tenders` (B4): a shop that dropped cards has no
 * card button. Recorded, never processed — tapping "Venmo" writes down that a
 * Venmo arrived.
 *
 * Under them, only when used: a gift card (the certificate lookup, out of the
 * mirror so it works offline), account credit, the visit's deposit, a split
 * payment and a tip. **The order the server applies them in is unchanged and
 * not on screen**: discount, certificate, deposit, credit (gift then cash), and
 * then these buttons for whatever is left. Each step caps at what remains, and
 * `money/waterfall.ts` — the server's function, transcribed — is what the
 * totals above were computed with.
 */
export interface PaymentProps {
  t: T;
  currency: string;
  locale: UiLocale;
  tillTenders: readonly RemainderKind[];
  chosen: RemainderKind | null;
  onChoose: (kind: RemainderKind) => void;
  tenderLabel: string;
  onTenderLabel: (text: string) => void;

  /* The split (0046). */
  extraTenders: readonly TenderDraft[];
  firstTenderText: string;
  onFirstTenderText: (text: string) => void;
  onAddPart: () => void;
  onChangePart: (key: string, apply: (part: TenderDraft) => TenderDraft) => void;
  onRemovePart: (key: string) => void;

  /* A gift card — `certificates` module only. */
  certificatesOn: boolean;
  certCode: string;
  onCertCode: (text: string) => void;
  certApplied: string | null;
  onLookUp: () => void;
  onForgetCertificate: () => void;
  certificate: Certificate | undefined;
  certificateState: CertificateState | null;
  certAmountText: string;
  onCertAmountText: (text: string) => void;

  /* The visit's deposit — a close-out only. */
  depositOffered: boolean;
  useDeposit: boolean;
  onUseDeposit: (on: boolean) => void;
  depositKnown: boolean;
  depositAvailableCents: number;

  /* Account credit — `credit` module and a named guest only. */
  creditOffered: boolean;
  useCredit: boolean;
  onUseCredit: (on: boolean) => void;
  creditGiftCents: number;
  creditCashCents: number;
  giftText: string;
  onGiftText: (text: string) => void;
  cashText: string;
  onCashText: (text: string) => void;

  /* A tip — `session.till.tips`, off by default. */
  tipsOn: boolean;
  tipText: string;
  onTipText: (text: string) => void;
  /** The pre-tax sale after discounts — what a percentage chip is taken of. */
  tipBaseCents: number;
}

export function Payment(p: PaymentProps): ReactNode {
  const { t } = p;
  const money = (cents: number): string => formatMoney(cents, p.currency, p.locale);
  const [giftOpen, setGiftOpen] = useState(p.certApplied !== null);
  const split = p.extraTenders.length > 0;

  return (
    <div className="pos-pay">
      <h3 className="pos-pay__title">{t('checkout.howPaidQuestion')}</h3>

      <TenderButtons
        t={t}
        tenders={p.tillTenders}
        chosen={p.chosen}
        onChoose={p.onChoose}
        label={split ? t('checkout.partTenderLabel', { n: 1 }) : t('checkout.howPaidQuestion')}
      />
      {p.chosen === 'other' ? (
        <input
          className="pos-pay__label"
          aria-label={t('checkout.tenderLabelLabel')}
          placeholder={t('checkout.tenderLabelPlaceholder')}
          maxLength={40}
          value={p.tenderLabel}
          onChange={(e) => p.onTenderLabel(e.target.value)}
        />
      ) : null}

      {/* The split (0046): the first payment's amount only once there is a second. */}
      {split ? (
        <div className="pos-split">
          <input
            aria-label={t('checkout.partAmountLabel', { n: 1 })}
            className="pos-split__amount"
            inputMode="decimal"
            placeholder="0.00"
            value={p.firstTenderText}
            onChange={(e) => p.onFirstTenderText(e.target.value)}
          />
          {p.extraTenders.map((part, index) => (
            <div className="pos-split__part" key={part.key}>
              <TenderButtons
                t={t}
                tenders={p.tillTenders}
                chosen={part.kind}
                onChoose={(kind) => p.onChangePart(part.key, (draft) => ({ ...draft, kind }))}
                label={t('checkout.partTenderLabel', { n: index + 2 })}
                small
              />
              <input
                aria-label={t('checkout.partAmountLabel', { n: index + 2 })}
                className="pos-split__amount"
                inputMode="decimal"
                /* The last row takes what is left, and the placeholder says so. */
                placeholder={index === p.extraTenders.length - 1 ? t('checkout.partRest') : '0.00'}
                value={part.text}
                onChange={(e) =>
                  p.onChangePart(part.key, (draft) => ({ ...draft, text: e.target.value }))
                }
              />
              {part.kind === 'other' ? (
                <input
                  aria-label={t('checkout.tenderLabelLabel')}
                  placeholder={t('checkout.tenderLabelPlaceholder')}
                  maxLength={40}
                  value={part.label}
                  onChange={(e) =>
                    p.onChangePart(part.key, (draft) => ({ ...draft, label: e.target.value }))
                  }
                />
              ) : null}
              <button
                type="button"
                className="link-button link-button--danger"
                onClick={() => p.onRemovePart(part.key)}
              >
                {t('checkout.removePayment')}
              </button>
            </div>
          ))}
          <small className="muted">{t('checkout.splitHint')}</small>
        </div>
      ) : null}

      {/* A gift card, looked up on the device. */}
      {p.certificatesOn && giftOpen ? (
        <div className="pos-pay__panel">
          <div className="pos-pay__row">
            <input
              aria-label={t('checkout.certCodeLabel')}
              placeholder={t('checkout.certCodePlaceholder')}
              value={p.certCode}
              autoCapitalize="characters"
              onChange={(e) => p.onCertCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') p.onLookUp();
              }}
            />
            <button type="button" className="button" onClick={p.onLookUp}>
              {t('checkout.lookUp')}
            </button>
          </div>
          {p.certApplied === null ? null : p.certificate === undefined ? (
            <small className="error">{t('checkout.noSuchCode')}</small>
          ) : p.certificateState === 'expired' ? (
            <small className="error">
              {t('checkout.certExpired', { date: p.certificate.expires_on ?? '' })}
            </small>
          ) : p.certificateState === 'redeemed' ? (
            <small className="error">{t('checkout.certEmpty')}</small>
          ) : (
            <small>
              {t('checkout.certLeft', {
                code: p.certificate.code,
                amount: money(p.certificate.remaining_cents),
              })}
              {p.certificate.item_name === null
                ? ''
                : t('checkout.certItem', { name: p.certificate.item_name })}
            </small>
          )}
          {p.certificate !== undefined && p.certificateState === 'open' ? (
            <input
              aria-label={t('checkout.certAmountLabel')}
              inputMode="decimal"
              placeholder={t('checkout.certAmountPlaceholder')}
              value={p.certAmountText}
              onChange={(e) => p.onCertAmountText(e.target.value)}
            />
          ) : null}
          <button
            type="button"
            className="link-button link-button--danger"
            onClick={() => {
              p.onForgetCertificate();
              setGiftOpen(false);
            }}
          >
            {t('checkout.forgetGiftCard')}
          </button>
        </div>
      ) : null}

      {/* Account credit: two pools, side by side, never summed (§7.4 rule 2). */}
      {p.creditOffered && p.useCredit ? (
        <div className="pos-pay__panel">
          <small>
            {t('checkout.creditPools', {
              gift: money(p.creditGiftCents),
              cash: money(p.creditCashCents),
            })}
          </small>
          <div className="pos-pay__row">
            <input
              aria-label={t('checkout.giftAmountLabel')}
              inputMode="decimal"
              placeholder={t('checkout.giftAmountPlaceholder')}
              value={p.giftText}
              onChange={(e) => p.onGiftText(e.target.value)}
            />
            <input
              aria-label={t('checkout.cashAmountLabel')}
              inputMode="decimal"
              placeholder={t('checkout.cashAmountPlaceholder')}
              value={p.cashText}
              onChange={(e) => p.onCashText(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="link-button link-button--danger"
            onClick={() => p.onUseCredit(false)}
          >
            {t('checkout.forgetCredit')}
          </button>
        </div>
      ) : null}

      {p.depositOffered ? (
        <label className="pos-pay__check">
          <input
            type="checkbox"
            aria-label={t('checkout.useDepositLabel')}
            checked={p.useDeposit}
            onChange={(e) => p.onUseDeposit(e.target.checked)}
          />
          <span>
            {t('checkout.useDepositLabel')}
            <small>
              {' '}
              {p.depositKnown
                ? t('checkout.depositTaken', { amount: money(p.depositAvailableCents) })
                : t('checkout.depositUnknown')}
            </small>
          </span>
        </label>
      ) : null}

      {p.tipsOn ? (
        <div className="pos-pay__panel">
          <div className="pos-pay__row">
            <span className="pos-pay__row-label">{t('checkout.tip')}</span>
            <input
              aria-label={t('checkout.tipLabel')}
              inputMode="decimal"
              placeholder="0.00"
              value={p.tipText}
              onChange={(e) => p.onTipText(e.target.value)}
            />
          </div>
          <div className="pos-chips">
            {TIP_PERCENT_CHIPS.map((percent) => (
              <button
                key={percent}
                type="button"
                className="pos-chip"
                /* Of the pre-tax sale after discounts — tipping on tax is
                   tipping the government. `tipFromPercent` is the domain's. */
                onClick={() =>
                  p.onTipText((tipFromPercent(p.tipBaseCents, percent) / 100).toFixed(2))
                }
              >
                {t('checkout.tipPercent', { percent })}
              </button>
            ))}
            <button type="button" className="pos-chip" onClick={() => p.onTipText('')}>
              {t('checkout.tipClear')}
            </button>
          </div>
          <small className="muted">{t('checkout.tipHint')}</small>
        </div>
      ) : null}

      {/* The quiet links: the less common ways, only when asked for. */}
      <div className="pos-pay__links">
        {p.certificatesOn && !giftOpen ? (
          <button type="button" className="link-button" onClick={() => setGiftOpen(true)}>
            {t('checkout.useGiftCard')}
          </button>
        ) : null}
        {p.creditOffered && !p.useCredit ? (
          <button type="button" className="link-button" onClick={() => p.onUseCredit(true)}>
            {t('checkout.useCreditLabel')}
          </button>
        ) : null}
        <button type="button" className="link-button" onClick={p.onAddPart}>
          {split ? t('checkout.addPayment') : t('checkout.splitPayment')}
        </button>
      </div>
    </div>
  );
}

/**
 * The tender buttons, as a radio group: one tap, and the answer is on screen.
 * Nothing starts chosen — a sale rung without anybody saying how it was paid
 * would record a tender somebody guessed.
 */
function TenderButtons({
  t,
  tenders,
  chosen,
  onChoose,
  label,
  small,
}: {
  t: T;
  tenders: readonly RemainderKind[];
  chosen: RemainderKind | null;
  onChoose: (kind: RemainderKind) => void;
  label: string;
  small?: boolean;
}): ReactNode {
  return (
    <div
      className={`pos-tenders${small === true ? ' pos-tenders--small' : ''}`}
      role="radiogroup"
      aria-label={label}
    >
      {tenders.map((kind) => (
        <button
          key={kind}
          type="button"
          role="radio"
          aria-checked={chosen === kind}
          className={`pos-tender${chosen === kind ? ' is-chosen' : ''}`}
          onClick={() => onChoose(kind)}
        >
          {t(`checkout.tenderButton.${kind}`)}
        </button>
      ))}
    </div>
  );
}
